import { type QuantitativeSize, type QuantitativeSizeString } from '../../types/sizes';
import { isQuantitativeSizeOfUnit, quantitativeSizeToString } from '../../util/sizes';

/**
 * Defines whether or not the body of the {@link EmptyMessage}, which includes the title,
 * description, content and action, should be justified at the top of the container or vertically
 * centered within the container.
 */
export type EmptyMessageJustify = 'center' | 'flex-start';

/**
 * Defines the flex gap between the title, description, content and action of the
 * {@link EmptyMessage}.
 */
export type EmptyMessageGap = number | QuantitativeSizeString<'px'>;

/**
 * Describes the density of the content inside of the {@link EmptyMessage} both as it relates to
 * the spacing between the content and the outer edges and the spacing of individual elements in
 * the content relative to each other.
 */
export type EmptyMessageDensity = 'compact' | 'standard';

/**
 * The class name that is applied to all content containers in the {@link EmptyMessage} component
 * that distribute content vertically (i.e. {@link Stack}) and for which the 'contentGap' prop
 * is applied.
 */
export const EmptyMessageDistributedContentClassName = 'empty-message-distributed-content';

export const DefaultEmptyMessageDensity: EmptyMessageDensity = 'standard';

export const EmptyMessageDensityProperties = {
  compact: { contentGap: 1, padding: 0.5 },
  standard: { contentGap: 2, padding: 3 },
} as const satisfies Record<EmptyMessageDensity, Record<string, number>>;

export const emptyMessageClassNameTargetedDensitySx = (density: EmptyMessageDensity) => ({
  [`& .${EmptyMessageDistributedContentClassName}`]: {
    gap: EmptyMessageDensityProperties[density].contentGap,
  },
  padding: EmptyMessageDensityProperties[density].padding,
});

/**
 * Returns the provided size, defaulted to the fallback if undefined, constrained such that if
 * provided as a pixel value, it will not exceed 100% of the container size to prevent overflow.
 */
export const constrainedDimension = (
  size: QuantitativeSize<'%' | 'px'> | undefined,
  fallback: QuantitativeSize<'%' | 'px'>,
) => {
  const value = size ?? fallback;
  if (isQuantitativeSizeOfUnit(value, '%')) {
    return value;
  }
  return `min(100%, ${quantitativeSizeToString(value, 'px')})`;
};
